import { atom } from "jotai";
import { RemittanceItem } from "@/core/entities/deduction/remittance";
import { AppConfig } from "@/infrastructure/services/config";
import { ITableSortColumn } from "@/core/interface";
import { DeductionCount } from "@/core/entities/deduction";
import { IRemittanceItemSummary } from "@/core/repositories/remittance";
import { IColumnAlias } from "@/core/repositories/deduction";
import { SelectOption } from "@ir/ui";
import { workspaceUserRoleNamesAtom } from "@/store/workspace";
import { brandMembersAtom } from "@/store/lookup";
import { currentUserAtom } from "@/store/user";
import { WorkspaceRoles } from "@/core/permissions";

const _AppConfig = AppConfig.getInstance();
const PAGINATION_PAGE_LIMIT = _AppConfig.PAGINATION_PAGE_LIMIT;

export interface DeductionCountState {
  data: DeductionCount;
  isLoading: boolean;
  error: Error | null;
}
interface RemittanceItemState {
  remittanceItems: RemittanceItem[];
  sortColumn: ITableSortColumn | null;
  isLoading: boolean;
  totalCount: number;
  currentPage: number;
  pageLimit: number;
  columns: IColumnAlias[];
  summary: IRemittanceItemSummary[] | null;
  error: Error | null;
  deductionCount: DeductionCountState;
}

const initialState: RemittanceItemState = {
  remittanceItems: [],
  sortColumn: null,
  isLoading: false,
  totalCount: 0,
  currentPage: 1,
  pageLimit: PAGINATION_PAGE_LIMIT,
  columns: [],
  summary: null,
  error: null,
  deductionCount: {
    data: new DeductionCount({
      all: 0,
      incomplete: 0,
      stages: [],
    }),
    // Start as true so FilterTabContent waits for the real count before
    // triggering the list fetch — prevents a premature fetch on mount.
    isLoading: true,
    error: null,
  },
};

// Base atoms
export const remittanceItemAtom = atom<RemittanceItemState>(initialState);

// Derived atoms for specific states
export const remittanceItemsAtom = atom(get => get(remittanceItemAtom).remittanceItems);
export const columnsAtom = atom(get => get(remittanceItemAtom).columns);
export const summaryAtom = atom(get => get(remittanceItemAtom).summary);
export const isLoadingAtom = atom(get => get(remittanceItemAtom).isLoading);
export const errorAtom = atom(get => get(remittanceItemAtom).error);
export const totalCountAtom = atom(get => get(remittanceItemAtom).totalCount);
export const currentPageAtom = atom(get => get(remittanceItemAtom).currentPage);
export const pageLimitAtom = atom(get => get(remittanceItemAtom).pageLimit);
export const sortColumnAtom = atom(get => get(remittanceItemAtom).sortColumn);
export const deductionCountAtom = atom(get => get(remittanceItemAtom).deductionCount);

// Derived atom: defaultAnalyst injection when the signed-in user is an analyst.
// Lives in the atoms file because it composes read-only state from multiple
// slices; no use case or repository is involved.
export const getDefaultAnalystAtom = atom<SelectOption | undefined>(get => {
  const workspaceUserRoleNames = get(workspaceUserRoleNamesAtom);
  const isAnalystUser = workspaceUserRoleNames.includes(WorkspaceRoles.ANALYST);
  const currentUser = get(currentUserAtom);
  const brandMembers = get(brandMembersAtom);
  const loggedInBrandMember = brandMembers?.data?.find(
    m => m.email.toLowerCase() === currentUser?.email?.toLowerCase()
  );

  if (isAnalystUser && loggedInBrandMember) {
    return {
      label: loggedInBrandMember.fullName,
      value: loggedInBrandMember.userId.toString(),
    };
  }
  return undefined;
});
